import { FetchResult } from '@apollo/client';
import { InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import {
  PRE_LITS_PLACEHOLDER_PLATFORM_SLA_ID,
  PRE_LITS_PLACEHOLDER_SERVICE_LEVEL,
} from '@provision/service-level-utils';
import { useCallback, useMemo } from 'react';
import {
  DynamicIcon,
  FormAutocomplete,
  FormAutocompleteProps,
  FormLabel,
  FormTextFieldProps,
  Tooltip,
} from 'src/components';
import { ServiceLevelSelectMenuField } from 'src/components/SidePanelTabs/service-level-select-menu-field';
import { BooleanSelectMenuField } from 'src/components/SidePanelTabs/tos-accepted-select-menu-field';
import { SAVE_DEBOUNCE_DELAY } from 'src/constants/delays';
import { componentsFontSizes } from 'src/constants/layout';
import {
  Address,
  Conversation,
  formatServiceLevelWithCpp,
  getServiceLevelValue,
  PartnerGroupRules,
  useAddress,
  useGroupRules,
  usePartnerGroupRules,
  UserGroupRules,
  useServiceLevels,
} from 'src/graphql';
import { getCorrectSubscriptionIdFromAddress } from 'src/graphql/entities/address/utils';
import { GetStripeSubscriptionDetailsResponse } from 'src/graphql/entities/stripe-subscription-details';
import {
  CreateStripeSubscriptionScheduleMutation,
  ScheduleCreationParams,
} from 'src/graphql/entities/stripe-subscription-schedule';
import { Modals, useShowModals } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { getMembershipInfoChanges, isCPPExpired } from './subscription-details.utils';

export interface MembershipInformationFormValues {
  platformSlaId: { id: string; label: string };
  tosAccepted: { id: string; label: string };
  membershipSalesNotes: string;
}

interface MembershipInfoFormProps {
  address: Partial<Address>;
  conversation?: Conversation;
  disabled?: boolean;
  addCppToSchedule?: (
    params: ScheduleCreationParams,
  ) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;
  isCppDisabled?: boolean;
  subscriptionDetails?: GetStripeSubscriptionDetailsResponse;
}

const tosAcceptedOptions = [
  { id: 'Yes', label: 'Accepted' },
  { id: 'No', label: 'Not Accepted' },
];

const membershipRegisteredOptions = [
  { id: 'Yes', label: 'Yes' },
  { id: 'No', label: 'No' },
];

export const useMembershipInfoForm = ({
  address,
  conversation,
  disabled,
  addCppToSchedule,
  isCppDisabled,
  subscriptionDetails,
}: MembershipInfoFormProps) => {
  const theme = useTheme();
  const { checkValidity, loading: loadingGroupRules } = useGroupRules();
  const {
    serviceLevelOptions,
    loading: loadingServiceLevels,
    hasServiceLevelAccess,
  } = useServiceLevels(address.partner?.id ?? address.partnerId);
  const { partnerGroupRules, loading: loadingPartnerGroupRules } = usePartnerGroupRules(
    address.partner?.id ?? address.partnerId ?? conversation?.partnerId,
  );
  const isBpoPartner = partnerGroupRules[PartnerGroupRules.AccessToOnevisionBpo];
  const { updateAddress } = useAddress(address.addressId);
  const { showSuccessAlert } = useNotifications();
  const [openAddComplimentaryPeriodModal] = useShowModals(Modals.AddComplimentaryPeriod);

  const onSubmit = useCallback(
    async (values: MembershipInformationFormValues) => {
      const changes = getMembershipInfoChanges(values, address, serviceLevelOptions);
      if (!changes) {
        return;
      }

      await updateAddress(
        {
          ...address,
          ...changes,
        },
        conversation?.conversationId,
        () => {
          if (changes.tosAccepted !== undefined) {
            showSuccessAlert('Membership information Updated');
          }
        },
      );
    },
    [
      address,
      conversation?.conversationId,
      showSuccessAlert,
      updateAddress,
      serviceLevelOptions,
    ],
  );

  const canUpdateSLA = checkValidity([UserGroupRules.CanUpdateSLAs]);

  const currentServiceLevel = useMemo(() => {
    const effectivePlatformSlaId = getServiceLevelValue({
      platformSlaId: address.platformSlaId,
      partner: address.partner ?? conversation?.partner,
      timeZone: address.partner?.timeZone ?? conversation?.partner?.timeZone!,
    });

    if (effectivePlatformSlaId === undefined) {
      return null;
    }

    if (effectivePlatformSlaId === PRE_LITS_PLACEHOLDER_PLATFORM_SLA_ID) {
      return {
        id: PRE_LITS_PLACEHOLDER_SERVICE_LEVEL,
        label: PRE_LITS_PLACEHOLDER_SERVICE_LEVEL,
      };
    }

    const displayName = effectivePlatformSlaId
      ? serviceLevelOptions.find((option) => option.platformSlaId === effectivePlatformSlaId)
          ?.label
      : undefined;
    const label = formatServiceLevelWithCpp(displayName, address.cppExpDate);

    return { id: label, label };
  }, [
    serviceLevelOptions,
    address.platformSlaId,
    address.cppExpDate,
    address.partner,
    conversation?.partner,
  ]);

  const addressSubscriptionId = useMemo(
    () => getCorrectSubscriptionIdFromAddress(address as Address),
    [address],
  );

  const currentTosAccepted = useMemo(() => {
    return tosAcceptedOptions.find((item) => item.id === address?.tosAccepted);
  }, [address?.tosAccepted]);

  const currentMembershipRegistered = useMemo(() => {
    return membershipRegisteredOptions.find((item) => item.id === address?.creditCardProvided);
  }, [address?.creditCardProvided]);

  const formFields = useMemo(() => {
    const commonProps = {
      autocompleteProps: {
        forcePopupIcon: false,
        disableClearable: true,
        disabled,
        sx: {
          padding: theme.spacing(0.5, 1),
          borderRadius: theme.shape.borderRadius / 8,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.custom.OV.sys.surface,
          },
          '& .MuiInput-root': {
            cursor: 'pointer',
          },
          '& .MuiInput-input': {
            userSelect: 'none',
            cursor: 'pointer',
            '&::selection': {
              background: 'transparent',
            },
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
          },
        },
        ListboxProps: {
          sx:
            theme.palette.mode === 'dark'
              ? {
                  background: theme.palette.custom.OV.sys.surfaceContainerHigh,
                  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                  borderRadius: theme.shape.borderRadius / 8,
                }
              : {},
        },
      },
      textFieldProps: {
        size: 'small',
      },
      inputProps: {
        sx: {
          ...theme.typography.body2,
          fontSize: theme.utils.important(componentsFontSizes.input),
        },
      },
      renderField,
    };

    const fields = [
      ...(hasServiceLevelAccess && isBpoPartner
        ? [
            {
              type: 'autocomplete',
              name: 'platformSlaId' as const,
              label: 'Service Level',
              required: 'Service Level is required',
              options: serviceLevelOptions,
              disabledTooltip: 'Site is on a subscription',
              defaultValue: currentServiceLevel,
              ...commonProps,
              renderField: (
                field: FormAutocompleteProps & { disabledTooltip?: string },
                formDisabled?: boolean,
              ) => (
                <ServiceLevelSelectMenuField
                  field={field}
                  serviceLevelOptions={serviceLevelOptions}
                  cppExpDate={address.cppExpDate}
                  disabled={formDisabled}
                />
              ),
              autocompleteProps: {
                ...commonProps.autocompleteProps,
                disabled: disabled || Boolean(!canUpdateSLA || addressSubscriptionId),
                loading: loadingServiceLevels,
              },
            },
          ]
        : []),
      ...(!isBpoPartner
        ? [
            {
              type: 'textfield',
              name: 'membership' as const,
              label: 'Membership',
              defaultValue:
                subscriptionDetails?.partnerProduct?.displayName ?? 'No membership',
              disabled: true,
              icon: (
                <DynamicIcon
                  name="id-card"
                  library="lucide"
                  color={theme.palette.custom.OV.sys.outline}
                />
              ),
              renderField: renderCPPField,
            },
          ]
        : []),
      {
        type: 'autocomplete',
        name: 'tosAccepted' as const,
        label: 'Terms of Service',
        required: 'Terms of Service is required',
        options: tosAcceptedOptions,
        defaultValue: currentTosAccepted,
        ...commonProps,
        renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
          <BooleanSelectMenuField field={field} disabled={formDisabled} />
        ),
      },
      {
        type: 'autocomplete',
        name: 'creditCardProvided' as const,
        label: 'Membership Registered',
        required: 'Terms of Service is required',
        options: membershipRegisteredOptions,
        defaultValue: currentMembershipRegistered,
        ...commonProps,
        renderField: (field: FormAutocompleteProps) => (
          <BooleanSelectMenuField field={field} disabled />
        ),
        autocompleteProps: {
          ...commonProps.autocompleteProps,
          disabled: true,
        },
      },
      {
        type: 'textfield',
        name: 'cppExpDate' as const,
        defaultValue: address?.cppExpDate || 'Not Found',
        label: 'CPP Expiration',
        disabled: disabled || !canUpdateSLA || isCppDisabled,

        onClick: () => {
          openAddComplimentaryPeriodModal({
            address: address as Address,
            addCppToSchedule,
          });
        },
        icon: address?.cppExpDate ? (
          <DynamicIcon
            name="calendar"
            library="lucide"
            color={
              isCPPExpired(address?.cppExpDate)
                ? theme.palette.custom.OV.sys.error
                : theme.palette.custom.OV.sys.success
            }
          />
        ) : undefined,
        renderField: renderCPPField,
      },
      {
        type: 'rich-text-bubble',
        label: 'Notes',
        name: 'membershipSalesNotes' as const,
        defaultValue: address?.membershipSalesNotes || '',
        disabled,
        sx: {
          marginTop: theme.spacing(2),
        },
        debounced: SAVE_DEBOUNCE_DELAY,
      },
    ] as FormAutocompleteProps[];

    return fields;
  }, [
    serviceLevelOptions,
    address,
    openAddComplimentaryPeriodModal,
    canUpdateSLA,
    currentMembershipRegistered,
    currentServiceLevel,
    subscriptionDetails?.partnerProduct?.displayName,
    currentTosAccepted,
    addCppToSchedule,
    disabled,
    theme,
    addressSubscriptionId,
    isCppDisabled,
    hasServiceLevelAccess,
    isBpoPartner,
    loadingServiceLevels,
  ]);

  return {
    onSubmit,
    formFields,
    loading: loadingServiceLevels || loadingGroupRules || loadingPartnerGroupRules,
  };
};

export function renderCPPField(field: FormTextFieldProps) {
  const { defaultValue, label, icon, onClick, disabled } = field;

  return (
    <Stack direction="row" alignItems="center" px={2} key={label}>
      <FormLabel label={label} />
      <Stack
        direction="row"
        alignItems="center"
        width="100%"
        gap={1}
        sx={{
          padding: '4px 8px',
          borderRadius: (theme) => theme.shape.borderRadius / 8,
          ...(!disabled && {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.custom.OV.sys.surface,
            },
          }),
        }}
        onClick={disabled ? undefined : onClick}
      >
        <Typography
          component="span"
          sx={{
            fontSize: componentsFontSizes.input,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {icon}
          {defaultValue}
        </Typography>
      </Stack>
    </Stack>
  );
}

function renderField(field: FormAutocompleteProps & { disabledTooltip?: string }) {
  const { type, defaultValue, label, icon, options, renderField, disabledTooltip, ...rest } =
    field;
  const isDisabled = field.autocompleteProps?.disabled || false;

  return (
    <Stack direction="row" alignItems="center" px={2} key={label}>
      <FormLabel label={label} />
      <Stack direction="row" width="100%" gap={1}>
        <Tooltip
          title={isDisabled && disabledTooltip ? disabledTooltip : ''}
          placement="bottom-start"
        >
          <span style={{ width: '100%' }}>
            <FormAutocomplete
              type={type}
              defaultValue={defaultValue}
              options={options}
              {...rest}
              inputProps={{
                ...rest.inputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    {typeof icon === 'string' ? <DynamicIcon name={icon} /> : icon}
                  </InputAdornment>
                ),
              }}
              disabled={isDisabled}
            />
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
